'use client';

import { useTranslations } from 'next-intl';
import {
  BottomBar,
  // DarkMoonSection,
  ProductSelectionSection,
  UltracareInfoSection,
} from 'components/ring-air';
import { Contents } from 'components/ring-air/Contents';
import { RingSelectionContextProvider } from 'components/ring-air/RingSelectionContext';
import { TechnicalSpecs } from 'components/technicalSpecs/TechnicalSpecs';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import {
  ClaimSizingKitPopup,
  ClaimSizingKitPopupHandle,
} from 'components/shared/ClaimSizingKitPopup';
import { shouldShowSizingKitPopup } from 'components/shared/ClaimSizingKitPopup/constants';
import { AirRingColors, RingSizes } from 'content/products/ring';
import { ringBuyPageFaqData_AE } from 'content/products/ring/faqData';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { SecondaryNavbar_RingAir } from './SecondaryNavbar';
import { BgBlurredText } from './BgBlurredText';
import { Capabilities } from './Capabilities';
import { ProductWizard } from '../shared/ProductWizard';
import { SolidBgTextSection } from './SolidBgTextSection';
import { UnderstandYourRecovery } from './UnderstandYourRecovery';
import { UnderstandYourSleep } from './UnderstandYourSleep';
import { NewComparison } from 'components/NewComparison';
import { ReviewsSection } from './ReviewsSection';
import { isReviewsSectionEnabled } from 'utils/featureFlags';
import { TravelChargerSequence } from './TravelChargerSequence';
import { ErrorBoundary } from 'components/shared/ErrorBoundary';
import { FAQSection } from 'components/shared/FAQSection';
import { ShippingEstimateObject } from './utils/types';
import { isProductAvailable } from 'lib/region/countryUtils';
import { Currency } from 'utils/Currency';
import { getOffersForRegion } from './utils/offersByRegion';

export const MCFRing = ({
  defaultSize,
  defaultColor,
  defaultUhx,
  estimates,
  region,
}: {
  defaultSize: RingSizes | null;
  defaultColor: AirRingColors | null;
  defaultUhx: boolean;
  estimates?: ShippingEstimateObject;
  store?: ShopifyStore;
  region?: string;
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { regionSlug, currency: regionCurrency, language } = useRegionLocale();
  const locale = searchParams?.get('locale');
  const t = useTranslations('ring');
  const sizingKitRef = useRef<ClaimSizingKitPopupHandle>(null);
  const showSizingKitPopup = shouldShowSizingKitPopup(region, 'ring_air');

  const currency = regionCurrency ?? Currency.eur;
  const reviewsEnabled = isReviewsSectionEnabled({ region, language });

  useEffect(() => {
    if (locale && !isProductAvailable(locale, 'ring')) {
      router.replace(`/${regionSlug}/ring/buy/global`);
    }
  }, [locale, router]);

  const { offersData, secondaryOfferData } = getOffersForRegion(
    region ?? locale?.toUpperCase() ?? 'DE',
    {
      t,
      regionSlug,
      router: {
        push: (url: string) => router.push(url),
        query: Object.fromEntries(searchParams?.entries() ?? []),
      },
    },
  );

  return (
    <>
      <RingSelectionContextProvider
        currency={currency}
        defaultSize={defaultSize}
        defaultColor={defaultColor ?? AirRingColors.RAW_TITANIUM}
        defaultUhx={defaultUhx}
        estimates={estimates}
        onCheckoutStarted={() => sizingKitRef.current?.cancelPopup()}
      >
        {/* Use Sections here make under /components/ring-pricing/your sections */}
        {/* from those - return <section></section> (styled) apply {gridMixin} in those and .content inside that will be grid-column: 2/-2 */}
        <ProductSelectionSection
          description={t('mCFRing.description.comfortableSleekSmart')}
          secondaryOfferData={secondaryOfferData}
          offersData={offersData}
        />
        <SecondaryNavbar_RingAir />
        <Contents htmlId="ring-contents" />
        <ErrorBoundary name="travel-charger-sequence">
          <TravelChargerSequence />
        </ErrorBoundary>
        {reviewsEnabled && <ReviewsSection htmlId="ring-reviews" />}
        <BgBlurredText />
        <UnderstandYourSleep htmlId="ring-sleep" />
        <UnderstandYourRecovery />
        <UltracareInfoSection />
        <NewComparison htmlId="ring-comparision" />
        <SolidBgTextSection />
        <Capabilities htmlId="ring-capabilities" />
        <TechnicalSpecs htmlId="ring-specs" />
        <ProductWizard title={t('ring.productWizard.contentA')} />
        <FAQSection faqData={ringBuyPageFaqData_AE} htmlId="ring-faq" />
        <BottomBar />
        {showSizingKitPopup && (
          <ClaimSizingKitPopup ref={sizingKitRef} region={region} />
        )}
      </RingSelectionContextProvider>
    </>
  );
};
